import { Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Users, ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import Countdown from "@/components/ateliers/Countdown";

function getStatus(w) {
  if (w.statut === "complet" || w.places_restantes <= 0) {
    return {
      label: "Complet",
      tone: "complet",
      dot: "bg-[#5F6170]",
      text: "text-[#5F6170]",
      bg: "bg-[#DDD7E2]/40",
      border: "border-[#DDD7E2]",
    };
  }
  if (w.places_restantes <= 3) {
    return {
      label: "Presque complet",
      tone: "presque",
      dot: "bg-[#C98FA3]",
      text: "text-[#C98FA3]",
      bg: "bg-[#F3EEF4]",
      border: "border-[#C98FA3]/40",
    };
  }
  return {
    label: "Places disponibles",
    tone: "ok",
    dot: "bg-[#3A2E6E]",
    text: "text-[#3A2E6E]",
    bg: "bg-[#F3EEF4]",
    border: "border-[#3A2E6E]/25",
  };
}

export default function CalCalendar({ workshops, onRegister }) {
  return (
    <section
      id="calendrier"
      data-testid="cal-calendar"
      className="py-24 md:py-32 bg-[#FAF7F8] scroll-mt-24"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <span className="text-sm uppercase tracking-[0.2em] text-[#C98FA3] font-medium">
              — Calendrier
            </span>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light mt-4 leading-[1.05]">
              Prochaines dates
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="flex items-center gap-5 text-xs text-[#5F6170]">
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#3A2E6E]" />
                Places disponibles
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#C98FA3]" />
                Presque complet
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#5F6170]" />
                Complet
              </span>
            </div>
          </Reveal>
        </div>

        {workshops && workshops.length > 0 ? (
          <div className="space-y-5">
            {workshops.map((w, i) => {
              const s = getStatus(w);
              const isComplet = s.tone === "complet";
              return (
                <Reveal key={w.id} delay={i * 0.07}>
                  <article
                    data-testid={`cal-card-${i}`}
                    className="group relative grid lg:grid-cols-12 gap-6 lg:gap-8 items-center p-7 md:p-9 bg-white rounded-3xl border border-[#DDD7E2] hover:border-[#C98FA3] hover:shadow-[0_20px_40px_rgb(40,71,184,0.06)] transition-all duration-300"
                  >
                    {/* Status badge */}
                    <div className="lg:col-span-3 flex flex-col gap-3">
                      <span
                        data-testid={`cal-status-${i}`}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${s.bg} ${s.border} ${s.text} text-xs uppercase tracking-[0.16em] font-medium w-fit`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                        {s.label}
                      </span>
                      {w.date_iso && !isComplet && <Countdown targetDate={w.date_iso} />}
                    </div>

                    {/* Main info */}
                    <div className="lg:col-span-6 lg:border-l lg:border-[#DDD7E2] lg:pl-8">
                      <h3 className="font-serif-display text-2xl md:text-3xl text-[#1E1E2A] leading-tight">
                        {w.date_label}
                      </h3>
                      <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#5F6170]">
                        <li className="inline-flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#C98FA3]" /> {w.lieu}
                        </li>
                        <li className="inline-flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#C98FA3]" /> {w.duree}
                        </li>
                        <li className="inline-flex items-center gap-2">
                          <Users className="w-4 h-4 text-[#C98FA3]" />
                          {isComplet
                            ? "0 place restante"
                            : `${w.places_restantes} places restantes`}
                        </li>
                      </ul>
                    </div>

                    {/* CTAs */}
                    <div className="lg:col-span-3 flex flex-col gap-2.5 lg:items-end">
                      <button
                        onClick={() => onRegister(w)}
                        disabled={isComplet}
                        data-testid={`cal-register-${i}`}
                        className="w-full lg:w-auto inline-flex items-center justify-between bg-[#2847B8] text-white hover:bg-[#3A2E6E] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 rounded-full px-6 py-3 font-medium text-sm gap-2"
                      >
                        {isComplet ? "Complet" : "S'inscrire"}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                      <Link
                        to="/constellations-en-groupe"
                        data-testid={`cal-info-${i}`}
                        className="w-full lg:w-auto inline-flex items-center justify-between text-[#3A2E6E] hover:text-[#2847B8] transition-colors text-sm font-medium px-6 py-2 gap-2"
                      >
                        En savoir plus
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        ) : (
          <div
            data-testid="cal-empty"
            className="p-12 bg-white rounded-3xl border border-[#DDD7E2] text-center"
          >
            <Calendar className="w-6 h-6 text-[#C98FA3] mx-auto" />
            <p className="mt-4 text-base text-[#5F6170]">
              Les prochaines dates seront annoncées prochainement.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
